import { useCallback, useEffect, useState } from "react";
import { demoApi, problemMessage } from "@/api/client";
import type { LabourReport } from "@/api/types";

export type LabourReportStatus = "loading" | "ready" | "error";

export interface UseLabourReportResult {
  /** `null` until resolved and on failure — never an empty report. A report with no
   *  lines is a real, different answer ("nothing was allocated in this window"), and
   *  collapsing "we do not know yet" into it would render a confident, empty table. */
  report: LabourReport | null;
  status: LabourReportStatus;
  error: string | null;
  /** Fetch a specific window. Passing nothing asks the server for its own default window. */
  load: (range?: { from: string; to: string }) => Promise<void>;
}

/**
 * The owner's labour-by-job-code report — GET /api/demo/reports/labour. ef-038-1.
 *
 * Owner-only SERVER-side (403 for an employee login). This hook does not check that and
 * must not — same reasoning as `useHoursReport`: a client-side role check is a rendering
 * decision, and treating it as the control is how the real one stops being maintained.
 *
 * `initialRange` seeds the FIRST fetch only (Labour.tsx reads it off `?from=&to=` in the
 * URL, exactly what `labour.spec.ts` navigates with) — every subsequent call goes through
 * `load()`. Omitted entirely, the first load asks the server for its own default window,
 * same discipline as `useHoursReport`'s own "the picker is seeded from what came back".
 *
 * A failure is NOT degraded to an empty report. There is no honest version of this screen
 * without figures, and an empty table for a window that truly has none is exactly what a
 * farmer would read as "nothing here" rather than "the request failed".
 */
export function useLabourReport(initialRange?: { from: string; to: string }): UseLabourReportResult {
  const [report, setReport] = useState<LabourReport | null>(null);
  const [status, setStatus] = useState<LabourReportStatus>("loading");
  const [error, setError] = useState<string | null>(null);

  const fetchInto = useCallback(
    async (range?: { from: string; to: string }, signal?: AbortSignal): Promise<void> => {
      setStatus("loading");
      try {
        const resolved = await demoApi.getLabourReport(range, signal);
        if (signal?.aborted) return;
        setReport(resolved);
        setStatus("ready");
        setError(null);
      } catch (err) {
        if (signal?.aborted || (err instanceof DOMException && err.name === "AbortError")) return;
        // The PREVIOUS report is cleared, not kept — a stale table under the new dates
        // just asked for is the one outcome that reads as an answer to that question.
        setReport(null);
        setStatus("error");
        setError(problemMessage(err, "Could not load the labour report."));
      }
    },
    [],
  );

  useEffect(() => {
    const controller = new AbortController();
    void fetchInto(initialRange, controller.signal);
    return () => controller.abort();
    // initialRange is read once, on mount — deliberately absent from the dependency
    // array. It answers "what did the URL ask for when this screen opened", not "what
    // should re-fetch whenever a new object literal is passed"; every later window
    // change goes through `load()` below instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchInto]);

  const load = useCallback(
    (range?: { from: string; to: string }) => fetchInto(range),
    [fetchInto],
  );

  return { report, status, error, load };
}
